import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Alert,
  Button,
  Card,
  Empty,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Progress,
  Space,
  Spin,
  Switch,
  Tag,
  Typography,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import './workspaceInformation/wiPhase1.css';

const { Title, Text, Paragraph } = Typography;

const API_BASE = process.env.NODE_ENV === 'production'
  ? 'https://admin.highperformr.ai/api'
  : 'http://localhost:4000/api';

const DEFAULT_THRESHOLD = 5000;

function usageStatus(percentage) {
  if (percentage >= 90) return { color: '#ff4d4f', status: 'exception' };
  if (percentage >= 70) return { color: '#faad14', status: 'normal' };
  return { color: '#52c41a', status: 'success' };
}

const VendorCredits = () => {
  const [vendors, setVendors] = useState([]);
  const [alertConfigs, setAlertConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [alertModal, setAlertModal] = useState(null);
  const [alertEmailsText, setAlertEmailsText] = useState('');
  const [alertThreshold, setAlertThreshold] = useState(DEFAULT_THRESHOLD);
  const [alertEnabled, setAlertEnabled] = useState(true);
  const [alertSaving, setAlertSaving] = useState(false);
  const [alertError, setAlertError] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const creditsRes = await axios.get(`${API_BASE}/vendor-credits`, {
        withCredentials: true,
      });
      setVendors(creditsRes.data);

      try {
        const alertsRes = await axios.get(`${API_BASE}/vendor-credits/alerts`, {
          withCredentials: true,
        });
        const byKey = {};
        (alertsRes.data || []).forEach((cfg) => {
          if (cfg.vendorKey) byKey[cfg.vendorKey] = cfg;
        });
        setAlertConfigs(byKey);
      } catch (alertErr) {
        console.error('Error fetching alert configs:', alertErr);
        setAlertConfigs({});
      }
    } catch (err) {
      console.error('Error fetching vendor credits:', err);
      setError('Failed to fetch vendor credits');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openAlertModal = (vendor) => {
    const existing = alertConfigs[vendor.constantName];
    setAlertModal(vendor);
    setAlertEmailsText((existing?.emails || []).join(', '));
    setAlertThreshold(
      typeof existing?.threshold === 'number' ? existing.threshold : DEFAULT_THRESHOLD,
    );
    setAlertEnabled(existing ? existing.enabled !== false : true);
    setAlertError(null);
  };

  const closeAlertModal = () => {
    if (alertSaving) return;
    setAlertModal(null);
    setAlertError(null);
  };

  const parseEmails = (text) => [
    ...new Set(
      text
        .split(/[,;\s]+/)
        .map((e) => e.trim().toLowerCase())
        .filter(Boolean),
    ),
  ];

  const saveAlertConfig = async () => {
    if (!alertModal?.constantName) return;
    const emails = parseEmails(alertEmailsText);
    if (emails.length === 0) {
      setAlertError('Add at least one email');
      return;
    }
    const threshold = Number(alertThreshold);
    if (!Number.isFinite(threshold) || threshold < 0) {
      setAlertError('Threshold must be a non-negative number');
      return;
    }

    try {
      setAlertSaving(true);
      setAlertError(null);
      const { data } = await axios.put(
        `${API_BASE}/vendor-credits/alerts/${encodeURIComponent(alertModal.constantName)}`,
        {
          emails,
          threshold,
          enabled: alertEnabled,
          vendorLabel: `${alertModal.vendor} - ${alertModal.apiKeyName || alertModal.constantName}`,
        },
        { withCredentials: true },
      );
      setAlertConfigs((prev) => ({ ...prev, [data.vendorKey]: data }));
      setAlertModal(null);
    } catch (err) {
      console.error('Error saving alert config:', err);
      setAlertError(err.response?.data?.error || 'Failed to save alert config');
    } finally {
      setAlertSaving(false);
    }
  };

  const removeAlertConfig = async () => {
    if (!alertModal?.constantName) return;
    try {
      setAlertSaving(true);
      setAlertError(null);
      await axios.delete(
        `${API_BASE}/vendor-credits/alerts/${encodeURIComponent(alertModal.constantName)}`,
        { withCredentials: true },
      );
      setAlertConfigs((prev) => {
        const next = { ...prev };
        delete next[alertModal.constantName];
        return next;
      });
      setAlertModal(null);
    } catch (err) {
      console.error('Error removing alert config:', err);
      setAlertError(err.response?.data?.error || 'Failed to remove alert config');
    } finally {
      setAlertSaving(false);
    }
  };

  const getUsagePercentage = (remaining, limit) => {
    if (!limit || limit === 0) return 0;
    return ((limit - remaining) / limit) * 100;
  };

  const formatResetTime = (seconds) => {
    if (!seconds) return 'N/A';
    const resetTime = new Date(Date.now() + (seconds * 1000));
    return resetTime.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return 'N/A';
    return new Date(isoString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const renderUsage = (vendor, index) => {
    const isProspeo = vendor.vendor === 'Prospeo';
    const isSearlo = vendor.vendor === 'Searlo';
    const isSerper = vendor.vendor === 'Serper';
    const isUltra = vendor.vendor === 'UltraAPIs';
    const isBetterContact = vendor.vendor === 'BetterContact';
    const isRecall = vendor.vendor === 'Recall';
    const isSerpex = vendor.vendor === 'Serpex';
    const isBalanceOnly = isSerper || isSearlo || isUltra || isBetterContact || isRecall;
    const creditsUsed = isProspeo || isSearlo
      ? vendor.creditsUsed
      : (vendor.creditsLimit - vendor.creditsRemaining);
    const usagePercentage = getUsagePercentage(vendor.creditsRemaining, vendor.creditsLimit);
    const remainingPercentage = vendor.creditsLimit ? 100 - usagePercentage : 0;
    const { color, status } = usageStatus(isSerpex ? (100 - remainingPercentage) : usagePercentage);

    if (vendor.error) {
      return <Text type="danger" strong>{vendor.error}</Text>;
    }
    if (isSerpex) {
      return (
        <div>
          <Flex justify="space-between" align="center" style={{ marginBottom: 8 }}>
            <Text strong>
              {vendor.creditsRemaining?.toLocaleString() ?? 'N/A'} / {vendor.creditsLimit?.toLocaleString() ?? 'N/A'}
            </Text>
            <Tag color={color}>{remainingPercentage.toFixed(1)}% left</Tag>
          </Flex>
          <Progress percent={Number(remainingPercentage.toFixed(1))} showInfo={false} strokeColor={color} status={status} size="small" />
          <Text type="secondary" style={{ fontSize: 11 }}>Remaining / total purchased</Text>
        </div>
      );
    }
    if (isBalanceOnly) {
      return (
        <Text strong style={{ fontSize: 18 }}>
          Balance:{' '}
          {isRecall
            ? (vendor.creditBalanceDisplay || vendor.creditsRemaining?.toLocaleString() || 'N/A')
            : (vendor.creditsRemaining?.toLocaleString() || 'N/A')}
        </Text>
      );
    }
    return (
      <div>
        <Flex justify="space-between" align="center" style={{ marginBottom: 8 }}>
          <Text strong>
            {creditsUsed?.toLocaleString() || 'N/A'} / {vendor.creditsLimit?.toLocaleString() || 'N/A'}
          </Text>
          <Tag color={color}>{usagePercentage.toFixed(1)}%</Tag>
        </Flex>
        <Progress percent={Number(usagePercentage.toFixed(1))} showInfo={false} strokeColor={color} status={status} size="small" />
      </div>
    );
  };

  const renderMeta = (vendor) => {
    const isProspeo = vendor.vendor === 'Prospeo';
    const isSearlo = vendor.vendor === 'Searlo';
    const isSerper = vendor.vendor === 'Serper';
    const isUltra = vendor.vendor === 'UltraAPIs';
    const isBetterContact = vendor.vendor === 'BetterContact';
    const isRecall = vendor.vendor === 'Recall';
    const isSerpex = vendor.vendor === 'Serpex';

    return (
      <Space direction="vertical" size={2}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Account: {vendor.accountEmail || '--'}
        </Text>
        {isProspeo ? (
          <>
            <Text strong>{vendor.currentPlan}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Renews in {vendor.nextQuotaRenewalDays} days
            </Text>
          </>
        ) : null}
        {isSearlo ? (
          <>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Expires: {formatDateTime(vendor.expiryDate)}
            </Text>
            {vendor.isLowBalance ? <Tag color="error">Low balance</Tag> : null}
          </>
        ) : null}
        {isSerper ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Today: {vendor.usageToday?.toLocaleString() || 'N/A'}
          </Text>
        ) : null}
        {isUltra ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Requests: {vendor.requestsRemaining?.toLocaleString() || 'N/A'}
            {vendor.requestsLimit ? ` / ${vendor.requestsLimit.toLocaleString()}` : ''}
          </Text>
        ) : null}
        {isSerpex ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Used: {vendor.creditsUsed?.toLocaleString() ?? 'N/A'}
          </Text>
        ) : null}
        {!isProspeo && !isSearlo && !isSerper && !isUltra && !isSerpex && !isBetterContact && !isRecall ? (
          <>
            <Text strong>
              Credits remaining: {vendor.creditsRemaining?.toLocaleString() || 'N/A'}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Resets: {formatResetTime(vendor.creditsReset)}
            </Text>
          </>
        ) : null}
      </Space>
    );
  };

  return (
    <div className="wi-page">
      <div className="wi-panel" style={{ maxWidth: 1100 }}>
        <Card bordered={false} className="wi-search-card">
          <Flex justify="space-between" align="flex-start" gap={16} wrap="wrap">
            <div>
              <Title level={3} style={{ margin: 0 }}>
                Vendor API Usage
              </Title>
              <Paragraph type="secondary" style={{ marginBottom: 0, marginTop: 4 }}>
                Credits, balances, and low-balance alert configuration
              </Paragraph>
            </div>
            <Button type="primary" icon={<ReloadOutlined />} onClick={loadData} loading={loading}>
              Refresh
            </Button>
          </Flex>
        </Card>

        {loading && vendors.length === 0 ? (
          <div style={{ marginTop: 48, textAlign: 'center' }}>
            <Spin size="large" tip="Loading vendor credits…" />
          </div>
        ) : null}

        {error ? (
          <Alert
            type="error"
            showIcon
            style={{ marginTop: 16 }}
            message={error}
            action={
              <Button size="small" onClick={loadData}>
                Retry
              </Button>
            }
          />
        ) : null}

        {!loading && !error && vendors.length === 0 ? (
          <Empty
            style={{ margin: '48px 0' }}
            description="Unable to retrieve vendor credit information."
          />
        ) : null}

        {vendors.length > 0 ? (
          <Space direction="vertical" size={16} style={{ width: '100%', marginTop: 16 }}>
            {vendors.map((vendor, index) => {
              const alertCfg = vendor.constantName ? alertConfigs[vendor.constantName] : null;
              return (
                <Card
                  key={vendor.constantName || index}
                  className="wi-details-card"
                  title={
                    <div>
                      <Text strong>
                        {vendor.vendor} - {vendor.apiKeyName || `Key ${index + 1}`}
                      </Text>
                      {vendor.constantName ? (
                        <div>
                          <Text type="secondary" code style={{ fontSize: 11 }}>
                            {vendor.constantName}
                          </Text>
                        </div>
                      ) : null}
                      {vendor.region ? (
                        <div>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {vendor.region}
                          </Text>
                        </div>
                      ) : null}
                      {vendor.apiKeyMasked ? (
                        <div>
                          <Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>
                            {vendor.apiKeyMasked}
                          </Text>
                        </div>
                      ) : null}
                    </div>
                  }
                  extra={
                    vendor.constantName ? (
                      <Button
                        type={alertCfg?.enabled ? 'primary' : 'default'}
                        ghost={!!alertCfg?.enabled}
                        onClick={() => openAlertModal(vendor)}
                      >
                        {alertCfg
                          ? `Alerts (${alertCfg.emails?.length || 0})`
                          : 'Configure alerts'}
                      </Button>
                    ) : null
                  }
                >
                  <Flex gap={24} wrap="wrap" align="flex-start">
                    <div style={{ flex: '1 1 220px', minWidth: 200 }}>{renderUsage(vendor, index)}</div>
                    <div style={{ flex: '1 1 220px', minWidth: 200 }}>{renderMeta(vendor)}</div>
                  </Flex>
                  {vendor.usedIn && vendor.usedIn.length > 0 ? (
                    <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--ant-color-split, #f0f0f0)' }}>
                      <Text type="secondary" strong style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
                        Used in
                      </Text>
                      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
                        {vendor.usedIn.map((usage, idx) => (
                          <li key={idx} style={{ marginBottom: 4 }}>
                            <Text type="secondary">{usage}</Text>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </Card>
              );
            })}
          </Space>
        ) : null}
      </div>

      <Modal
        open={!!alertModal}
        title="Alert recipients"
        onCancel={closeAlertModal}
        maskClosable={!alertSaving}
        footer={
          <Flex justify="space-between" wrap="wrap" gap={8}>
            <div>
              {alertModal && alertConfigs[alertModal.constantName] ? (
                <Button danger onClick={removeAlertConfig} loading={alertSaving}>
                  Remove
                </Button>
              ) : null}
            </div>
            <Space>
              <Button onClick={closeAlertModal} disabled={alertSaving}>
                Cancel
              </Button>
              <Button type="primary" onClick={saveAlertConfig} loading={alertSaving}>
                Save
              </Button>
            </Space>
          </Flex>
        }
      >
        {alertModal ? (
          <Form layout="vertical">
            <Paragraph type="secondary" style={{ marginTop: 0 }}>
              {alertModal.vendor} — {alertModal.apiKeyName || alertModal.constantName}
              <br />
              <Text code style={{ fontSize: 11 }}>
                {alertModal.constantName}
              </Text>
            </Paragraph>
            {alertError ? (
              <Alert type="error" showIcon message={alertError} style={{ marginBottom: 16 }} />
            ) : null}
            <Form.Item label="Emails (comma-separated)" required>
              <Input.TextArea
                value={alertEmailsText}
                onChange={(e) => setAlertEmailsText(e.target.value)}
                rows={3}
                placeholder="ops@highperformr.ai, finance@highperformr.ai"
              />
            </Form.Item>
            <Form.Item label="Threshold (notify when remaining < this)" required>
              <InputNumber
                min={0}
                value={alertThreshold}
                onChange={(v) => setAlertThreshold(v == null ? '' : v)}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item label="Alerts enabled">
              <Switch checked={alertEnabled} onChange={setAlertEnabled} />
            </Form.Item>
          </Form>
        ) : null}
      </Modal>
    </div>
  );
};

export default VendorCredits;
